import { useMemo } from 'react';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { useTranslation } from 'react-i18next';
import ContractInfoList, {
  parseLegacyContractSnapshot,
} from '@/components/Approval/@components/ContractInfo';
import InfoSection from '@/components/Approval/@components/InfoSection';
import useContractDraftSource, {
  type ContractDraftSourceErrorKind,
} from '@/hooks/approval/useContractDraftSource';
import type {
  ContractApprovalAttributes,
  ElawContract,
} from '@/types/approval';
import { parseContractIds } from '@/utils/approval/contractIds';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

const useErrorMessage = (kind: ContractDraftSourceErrorKind | undefined) => {
  const { t } = useTranslation();
  switch (kind) {
    case 'forbidden-contract':
      return t(
        '열람 권한이 없는 계약이 포함되어 계약 정보를 표시할 수 없습니다.',
      );
    case 'elaw-unavailable':
      return t(
        '법틀 연동 장애로 계약 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
      );
    case 'invalid-request':
    case 'unknown':
      return t('계약 정보를 불러오지 못했습니다.');
    default:
      return null;
  }
};

/**
 * 117·118 계약 품의 — 작성 시점 계약 정보로 그림(결재자·참조자도 같은 표).
 * 1) contractData(서버 저장 스냅숏) 2) 레거시 문서는 attributes.content 의 저장 표
 * 3) 둘 다 없는 예전 V2 문서만 법틀 재조회. 1)·2) 의 상세보기(법틀 링크)는 만료되는 값이라 계약별 권한 조회(partial)로 붙이고,
 * 보는 사람이 법틀 권한이 없는 계약은 상세보기 없음
 */
const ContractSlot = ({ data }: ContentExtraProps) => {
  const { t } = useTranslation();
  const attributes = data.attributes as ContractApprovalAttributes | null;
  const contractIds = useMemo(
    () => parseContractIds(attributes?.contractIds),
    [attributes?.contractIds],
  );
  const saved = useMemo<ElawContract[] | null>(() => {
    if (attributes?.contractData?.length) return attributes.contractData;
    const legacy = parseLegacyContractSnapshot(
      attributes?.content,
      contractIds,
    );
    return legacy.length ? legacy : null;
  }, [attributes?.contractData, attributes?.content, contractIds]);

  const linkSource = useContractDraftSource(
    saved ? data.apvType : null,
    contractIds,
    { partial: true },
  );
  const refetched = useContractDraftSource(
    !saved && contractIds.length ? data.apvType : null,
    contractIds,
  );
  const errorMessage = useErrorMessage(refetched.errorKind);

  const savedWithLinks = useMemo(() => {
    if (!saved) return saved;
    const links = new Map(
      (linkSource.contractResults ?? [])
        .filter((result) => result.access === 'full')
        .map((result) => [result.contractId, result.contract?.accessLink]),
    );
    return saved.map((c) => ({
      ...c,
      accessLink: links.get(c.contractId) ?? null,
    }));
  }, [saved, linkSource.contractResults]);

  if (!contractIds.length && !saved) return null;

  const title = t('계약 정보');
  if (savedWithLinks) {
    return (
      <ContentSlotRoot>
        <ContractInfoList title={title} contracts={savedWithLinks} />
      </ContentSlotRoot>
    );
  }

  let notice: string | null = null;
  if (errorMessage) notice = errorMessage;
  else if (refetched.isLoading || !refetched.contracts)
    notice = t('계약 정보를 불러오는 중입니다…');
  else if (!refetched.contracts.length)
    notice = t('선택한 계약을 찾을 수 없습니다.');

  return (
    <ContentSlotRoot>
      {notice ? (
        <InfoSection title={title}>
          <ApprovalNoticeBox role={errorMessage ? 'alert' : 'status'}>
            {notice}
          </ApprovalNoticeBox>
        </InfoSection>
      ) : (
        <ContractInfoList title={title} contracts={refetched.contracts ?? []} />
      )}
    </ContentSlotRoot>
  );
};

export default ContractSlot;
